import React, { useState } from "react";
import Navbar from "../components/Navbar";
import QrCard from "../components/QrCard";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { NbButton, NbInput, NbTextarea, NbSelect } from "../components/nb";
import { Plus, X, BadgeCheck, Camera } from "lucide-react";

const INTERESTS = ["Travel", "Music", "Foodie", "Fitness", "Art", "Gaming", "Reading", "Movies", "Coffee", "Hiking", "Photography", "Dancing", "Yoga", "Cooking", "Pets", "Wine"];

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function MyProfile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user.name || "", bio: user.bio || "", age: user.age || "", gender: user.gender || "",
    orientation: user.orientation || "", location: user.location || "", occupation: user.occupation || "",
    education: user.education || "", looking_for: user.looking_for || "",
    interests: user.interests || [], photos: user.photos || [], avatar: user.avatar || "",
    privacy_hidden: user.privacy_hidden || false,
  });
  const [saving, setSaving] = useState(false);

  const toggleInterest = (i) => {
    setForm((f) => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] }));
  };

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files).slice(0, 6);
    const b64 = await Promise.all(files.map(fileToBase64));
    setForm((f) => ({ ...f, photos: [...f.photos, ...b64].slice(0, 6), avatar: f.avatar || b64[0] }));
  };

  const handleAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const b64 = await fileToBase64(file);
    setForm((f) => ({ ...f, avatar: b64 }));
  };

  const removePhoto = (idx) => setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, age: form.age ? parseInt(form.age) : null };
      const { data } = await api.put("/profile", payload);
      setUser((u) => ({ ...u, ...data }));
      toast.success("Profile saved!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-head font-black text-4xl tracking-tighter mb-1">My Profile {user.verified_badge && <BadgeCheck className="w-7 h-7 inline text-[#D4AF37]" fill="#0A0A0A" />}</h1>
        <p className="text-gray-600 font-medium mb-8">This is what others see when they scan your QR.</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <QrCard user={user} compact />
            <div className="bg-white nb-border nb-shadow p-5">
              <h3 className="font-head font-black text-lg mb-3">Avatar</h3>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 nb-border bg-[#F3F3F1] overflow-hidden flex items-center justify-center">
                  {form.avatar ? <img src={form.avatar} alt="avatar" className="w-full h-full object-cover" /> : <Camera className="w-8 h-8 text-gray-400" />}
                </div>
                <label className="cursor-pointer bg-[#D4AF37] nb-border nb-shadow-sm nb-hover px-3 py-2 font-head font-bold text-xs uppercase" data-testid="avatar-upload-label">
                  Upload
                  <input type="file" accept="image/*" className="hidden" onChange={handleAvatar} data-testid="avatar-upload" />
                </label>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white nb-border nb-shadow p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="font-head font-bold text-sm uppercase">Name</label><NbInput data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div><label className="font-head font-bold text-sm uppercase">Age</label><NbInput data-testid="profile-age" type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="mt-1" /></div>
            </div>
            <div><label className="font-head font-bold text-sm uppercase">Bio / About Me</label><NbTextarea data-testid="profile-bio" rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1" placeholder="Tell people about yourself..." /></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><label className="font-head font-bold text-sm uppercase">Gender</label>
                <NbSelect data-testid="profile-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="mt-1">
                  <option value="">Select</option><option>Woman</option><option>Man</option><option>Non-binary</option><option>Other</option>
                </NbSelect></div>
              <div><label className="font-head font-bold text-sm uppercase">Orientation</label>
                <NbSelect data-testid="profile-orientation" value={form.orientation} onChange={(e) => setForm({ ...form, orientation: e.target.value })} className="mt-1">
                  <option value="">Select</option><option>Straight</option><option>Gay</option><option>Lesbian</option><option>Bisexual</option><option>Pansexual</option><option>Asexual</option>
                </NbSelect></div>
              <div><label className="font-head font-bold text-sm uppercase">Looking For</label>
                <NbSelect data-testid="profile-lookingfor" value={form.looking_for} onChange={(e) => setForm({ ...form, looking_for: e.target.value })} className="mt-1">
                  <option value="">Select</option><option>Relationship</option><option>Casual</option><option>Friends</option><option>Networking</option>
                </NbSelect></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="font-head font-bold text-sm uppercase">Location</label><NbInput data-testid="profile-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" placeholder="City" /></div>
              <div><label className="font-head font-bold text-sm uppercase">Occupation</label><NbInput data-testid="profile-occupation" value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} className="mt-1" /></div>
            </div>
            <div><label className="font-head font-bold text-sm uppercase">Education</label><NbInput data-testid="profile-education" value={form.education} onChange={(e) => setForm({ ...form, education: e.target.value })} className="mt-1" /></div>

            <div>
              <label className="font-head font-bold text-sm uppercase">Interests</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {INTERESTS.map((i) => (
                  <button key={i} type="button" onClick={() => toggleInterest(i)} data-testid={`interest-${i}`} className={`px-3 py-1.5 nb-border text-sm font-head font-bold uppercase nb-hover ${form.interests.includes(i) ? "bg-[#F3E8E6]" : "bg-white"}`}>{i}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="font-head font-bold text-sm uppercase">Photo Gallery (up to 6)</label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-2">
                {form.photos.map((p, i) => (
                  <div key={i} className="relative aspect-square nb-border overflow-hidden group">
                    <img src={p} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`} className="absolute top-1 right-1 bg-[#EF4444] text-white nb-border p-1"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                {form.photos.length < 6 && (
                  <label className="aspect-square nb-border bg-[#F3F3F1] flex flex-col items-center justify-center cursor-pointer nb-hover" data-testid="add-photo-label">
                    <Plus className="w-6 h-6" /><span className="text-xs font-bold uppercase">Add</span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotos} data-testid="add-photo" />
                  </label>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between bg-[#F3F3F1] nb-border p-4">
              <div><div className="font-head font-bold uppercase text-sm">Private Profile</div><div className="text-xs text-gray-600">Hide details from public QR scans & browse.</div></div>
              <button onClick={() => setForm({ ...form, privacy_hidden: !form.privacy_hidden })} data-testid="privacy-toggle" className={`w-14 h-8 nb-border relative ${form.privacy_hidden ? "bg-[#8B1E3F]" : "bg-white"}`}>
                <span className={`absolute top-0.5 w-6 h-6 bg-black transition-all ${form.privacy_hidden ? "left-7" : "left-0.5"}`} />
              </button>
            </div>

            <NbButton onClick={save} disabled={saving} data-testid="profile-save" className="w-full">{saving ? "Saving..." : "Save Profile"}</NbButton>
          </div>
        </div>
      </div>
    </div>
  );
}
